import { Combobox, Decorators, Dictionary, EntityGrid, Fluent, GridUtils, LookupEditor, LookupEditorOptions, QuickFilterBar, SelectEditor, ServiceResponse, ToolButton, Widget, WidgetProps, confirmDialog, faIcon, isTrimmedEmpty, localText, notifyError, notifySuccess, serviceCall, stringFormat, stripDiacritics } from "@serenity-is/corelib";
import { TranslationItem, TranslationListResponse, TranslationUpdateRequest, TranslationUpdateResponse } from "@serenity-is/extensions";
import { Column } from "@serenity-is/sleekgrid";
import copyToClipboard from "../Tools/CopyToClipboard";

@Decorators.registerClass('Serenity.Extensions.TranslationGrid')
export class TranslationGrid<P = {}> extends EntityGrid<TranslationItem, P> {
    protected getIdProperty() { return "Key"; }
    protected getLocalTextDbPrefix() { return "Site.Translation."; }
    protected getService() { return "Administration/Translation" }

    declare private hasChanges: boolean;
    declare private searchText: string;
    declare private sourceLanguage: LookupEditor;
    declare private targetLanguage: LookupEditor;
    declare private targetLanguageKey: string;

    constructor(props: WidgetProps<P>) {
        super(props);

        this.element.on('keyup.' + this.uniqueName + ' change.' + this.uniqueName,
            'input.custom-text', (e: Event) => {
                var value = Fluent(e.target).val()?.trim() || null;
                this.view.getItemById(Fluent(e.target).data('key')).CustomText = value;
                this.hasChanges = true;
            });
    }

    protected onClick(e: Event, row: number, cell: number): any {
        super.onClick(e, row, cell);

        if (Fluent.isDefaultPrevented(e)) {
            return;
        }

        let item = this.itemAt(row);
        let done: () => void;

        if (Fluent(e.target).hasClass('source-text')) {
            e.preventDefault();

            done = () => {
                item.CustomText = item.SourceText;
                this.view.updateItem(item.Key, item);
                this.hasChanges = true;
            };

            if (isTrimmedEmpty(item.CustomText) ||
                (item.CustomText.trim() === (item.SourceText?.trim() ?? ''))) {
                done();
                return;
            }

            confirmDialog(localText('Site.Translation.OverrideConfirmation'), done);
            return;
        }

        if (Fluent(e.target).hasClass('target-text')) {
            e.preventDefault();

            done = () => {
                item.CustomText = item.TargetText;
                this.view.updateItem(item.Key, item);
                this.hasChanges = true;
            };

            if (isTrimmedEmpty(item.CustomText) ||
                (item.CustomText.trim() === (item.TargetText?.trim() ?? ''))) {
                done();
                return;
            }

            confirmDialog(localText('Site.Translation.OverrideConfirmation'), done);
            return;
        }
    }

    protected getColumns(): Column[] {

        return [{
            field: 'Key',
            width: 300,
            sortable: false
        }, {
            field: 'SourceText',
            width: 300,
            sortable: false,
            format: ctx => <a class="source-text">{ctx.value}</a>,
        }, {
            field: 'CustomText',
            width: 300,
            sortable: false,
            format: ctx => <input class="custom-text" value={ctx.value ?? ""} type="text" data-key={ctx.item.Key} />
        }, {
            field: 'TargetText',
            width: 300,
            sortable: false,
            format: ctx => <a class="target-text">{ctx.value}</a>
        }];
    }

    protected createToolbarExtensions(): void {
        super.createToolbarExtensions();

        let opt: LookupEditorOptions = {
            lookupKey: 'Administration.Language'
        };

        this.sourceLanguage = Widget.create({
            type: LookupEditor,
            element: el => el.appendTo(this.toolbar.domNode).attr('placeholder', '--- ' +
                localText('Site.Translation.SourceLanguage') + ' ---'),
            options: opt
        });

        this.sourceLanguage.changeSelect2(e => {
            if (this.hasChanges) {
                this.saveChanges(this.targetLanguageKey).then(() => this.refresh());
            }
            else {
                this.refresh();
            }
        });

        this.targetLanguage = Widget.create({
            type: LookupEditor,
            element: el => el.appendTo(this.toolbar.domNode).attr('placeholder', '--- ' +
                localText('Site.Translation.TargetLanguage') + ' ---'),
            options: opt
        });

        this.targetLanguage.changeSelect2(e => {
            if (this.hasChanges) {
                this.saveChanges(this.targetLanguageKey).then(() => this.refresh());
            }
            else {
                this.refresh();
            }
        });
    }

    protected getQuickFilters() {
        var filters = super.getQuickFilters();

        var userTranslated = QuickFilterBar.boolean('UserTranslated',
            localText('Site.Translation.UserTranslated'));
        userTranslated.init = w => w.changeSelect2(() => this.hasChanges && this.saveChanges(this.targetLanguageKey));

        filters.push(userTranslated);

        var hasTranslation = QuickFilterBar.boolean('HasTranslation',
            localText('Site.Translation.HasTranslation'));
        hasTranslation.init = w => w.changeSelect2(() => this.hasChanges && this.saveChanges(this.targetLanguageKey));

        filters.push(hasTranslation);

        filters.push({
            field: 'Assembly',
            cssClass: 'Assembly',
            title: localText('Site.Translation.Assembly'),
            type: SelectEditor
        });

        return filters;
    }

    declare private keysByAssembly: Dictionary<Dictionary<boolean>>;

    protected onViewProcessData(response: TranslationListResponse) {
        response = super.onViewProcessData(response);
        this.keysByAssembly = {};
        var assembly = this.tryFindQuickFilter(SelectEditor, "Assembly");
        if (assembly) {
            var value = assembly.value;
            assembly.clearItems();
            var asmNames = Object.keys(response.KeysByAssembly);
            asmNames.sort();
            for (var asmName of asmNames) {
                var asmKeys = response.KeysByAssembly[asmName];
                if (!asmKeys?.length)
                    continue;
                assembly.addOption(asmName, asmName);
                var keys = this.keysByAssembly[asmName] = {};
                for (var key of asmKeys)
                    keys[key] = true;
            }
            Combobox.getInstance(assembly.domNode)?.setValue(value);
        }
        return response;
    }

    protected saveChanges(language: string): PromiseLike<any> {
        var translations: { [key: string]: string } = {};
        for (let item of this.getItems()) {
            translations[item.Key] = item.CustomText;
        }

        return serviceCall<ServiceResponse>({
            service: this.getService() + "/Update",
            request: {
                TargetLanguageID: language,
                Translations: translations
            } satisfies TranslationUpdateRequest
        }).then((response: TranslationUpdateResponse) => {
            this.hasChanges = false;
            notifySuccess(stringFormat(localText('Site.Translation.SaveSuccessMessage'), response.SavedPath));
        });
    }

    protected onViewSubmit(): boolean {
        var request = this.view.params;
        request.SourceLanguageID = this.sourceLanguage.value;
        this.targetLanguageKey = this.targetLanguage.value || '';
        request.TargetLanguageID = this.targetLanguageKey;
        this.hasChanges = false;
        return super.onViewSubmit();
    }

    protected getButtons(): ToolButton[] {
        return [
            {
                title: localText('Site.Translation.SaveChangesButton'),
                onClick: _ => this.saveChanges(this.targetLanguageKey).then(() => this.refresh()),
                cssClass: 'apply-changes-button'
            },
            {
                hint: localText("Site.Translation.CopySourceTranslations"),
                cssClass: 'copy-source-to-clipboard',
                icon: faIcon("clone"),
                onClick: () => {
                    var length = this.slickGrid.getDataLength();
                    var d = {};
                    for (var i = 0; i < length; i++) {
                        var item = this.slickGrid.getDataItem(i) as TranslationItem;
                        d[item.Key] = item.SourceText;
                    }

                    copyToClipboard(JSON.stringify(d, null, '  '))
                        .then(() => notifySuccess(localText("Site.Translation.CopySuccessMessage")))
                        .catch(() => notifyError(localText("Site.Translation.CopyFailMessage")))
                }
            },
            {
                hint: localText("Site.Translation.CopyTargetTranslations"),
                cssClass: 'copy-target-to-clipboard',
                icon: faIcon("copy"),
                onClick: () => {
                    var length = this.slickGrid.getDataLength();
                    var d = {};
                    for (var i = 0; i < length; i++) {
                        var item = this.slickGrid.getDataItem(i) as TranslationItem;
                        d[item.Key] = item.CustomText?.length ? item.CustomText :
                            item.HasTranslation ? item.TargetText : null;
                    }

                    copyToClipboard(JSON.stringify(d, null, '  '))
                        .then(() => notifySuccess(localText("Site.Translation.CopySuccessMessage")))
                        .catch(() => notifyError(localText("Site.Translation.CopyFailMessage")))
                }
            }
        ];
    }

    protected createQuickSearchInput() {
        GridUtils.addQuickSearchInputCustom(this.toolbar.domNode,
            (_, searchText, done) => {
                this.searchText = searchText;
                this.view.setItems(this.view.getItems(), true);
                done(this.slickGrid.getDataLength() > 0);
            });
    }

    protected onViewFilter(item: TranslationItem) {
        if (!super.onViewFilter(item))
            return false;

        var userTranslated = this.tryFindQuickFilter(SelectEditor, "UserTranslated");
        if (userTranslated?.value?.length) {
            if (userTranslated.value == "1" && !item.UserTranslated)
                return false;

            if (userTranslated.value == "0" && item.UserTranslated)
                return false;
        }

        var hasTranslation = this.tryFindQuickFilter(SelectEditor, "HasTranslation");
        if (hasTranslation?.value?.length) {
            if (hasTranslation.value == "1" && !item.HasTranslation)
                return false;

            if (hasTranslation.value == "0" && item.HasTranslation)
                return false;
        }

        var assembly = this.tryFindQuickFilter(SelectEditor, "Assembly");
        if (assembly?.value?.length) {
            var kba = (this.keysByAssembly || {})[assembly.value];
            if (!kba || !kba[item.Key])
                return false;
        }

        if (!this.searchText)
            return true;

        var searching = stripDiacritics(this.searchText).toLowerCase();

        function match(str: string) {
            if (!str)
                return false;

            return str.toLowerCase().indexOf(searching) >= 0;
        }

        return !searching || match(item.Key) || match(item.SourceText) ||
            match(item.TargetText) || match(item.CustomText);
    }

    protected usePager() {
        return false;
    }
}